// GraphQL client. Deliberately tiny — one POST with a bearer token. A cache
// library would add a layer between the numbers on screen and the query that
// fetched them, and "which number am I looking at" is the whole problem this
// project exists to fix.

export type GospelMonth = {
  month: string
  actualAr: string
  actualFundraise: string
  actualAp: string | null
  forecastNetNewAr: string
  forecastNetNewAp: string
  netCashDifference: string
  arBasis: string
  apBasis: string
  // Plan baseline payroll (negative, like actualAp) for a month with no
  // actuals yet — set only when apBasis === 'plan'; null otherwise.
  planPayroll: string | null
}

export type BalanceRow = {
  month: string
  auditedBalance: string
  source: string
  computedBalance: string
  drift: string
}

export type OverlapRow = {
  kind: string
  businessLine: string
  prospect: string
  month: string
  expectedAmount: string
  weightedAmount: string
}

// A source sheet failing its own arithmetic, located to the cell. Every figure
// is a block subtotal; the gap between two can be one row's amount, which is
// why this is shown only to people who can already open the sheet (backend
// migration 20261001110000).
export type SourceIssue = {
  check: string // ROWS_VS_SUBTOTAL | SHEET_ARITHMETIC
  label: string // the sheet's own A|B label, e.g. 'VENDOR|Sub Total'
  month: string
  tab: string
  cell: string | null
  cellUrl: string | null
  statedValue: string
  computedValue: string
  blockSubtotalCell: string | null
  blockSubtotalCellUrl: string | null
  blockSubtotalValue: string | null
}

export type Freshness = {
  job: string
  lastSucceededAt: string | null
  lastRunAt: string | null
  lastRunStatus: string | null
  stale: boolean
  issues: SourceIssue[]
}

export type SoftwarePlMonth = {
  month: string
  softwareRevenue: string
  // Null when suppressed for k-anonymity (a single-worker SW month).
  softwareCost: string | null
  softwareMargin: string | null
}

// Two running balances on one month-end anchor (migration 20261015100000):
// cashPosition includes weighted pipeline (deals not yet signed); the
// *ExPipeline pair is booked AR, closed fundraise, payroll and planned costs
// only. anchorMonth is an ENDED month; anchorSource is 'freee' for the synced
// bank feed, anything else for a hand-entered balance.
export type CashPositionMonth = {
  month: string
  netCashDifference: string
  cashPosition: string
  anchorMonth: string
  netCashDifferenceExPipeline: string
  cashPositionExPipeline: string
  anchorSource: string
  // Month-end actuals (GTM-54). Only the "Month-end actuals" view (on by
  // default) reads these; the fields above stay as they were. pointKind is 'actual' (a
  // month-end bank balance is known), 'missing' (the month ended in JST with
  // none) or 'projected' (not ended: projected from the latest actual).
  monthEnded: boolean
  actualBalance: string | null
  // 'audited' (hand-entered) or 'freee_month_end' (computed from bank transactions).
  actualSource: string | null
  pointKind: string
  projectionAnchorMonth: string | null
  projectedBalance: string | null
  projectedBalanceExPipeline: string | null
}

// Plan vs actual payroll: whether the headcount plan for months that already
// happened shows up in what freee paid, on the "delta vs plan hires" basis.
// Money is aggregate + plan only; a k-anon-suppressed month arrives with the
// freee figures null and status 'insufficient_data'.
export type ReconciliationMonth = {
  month: string
  freeeSalaryCost: string | null
  anchorMonth: string | null
  freeeAnchorCost: string | null
  actualGrowth: string | null
  plannedGrowth: string | null
  divergence: string | null
  // 'reconciled' | 'under_plan' | 'over_plan' | 'insufficient_data'
  status: string
}

export type HeadcountHire = {
  role: string
  annualSalary: string
  benefitsRate: string
  startMonth: string
}

export type HeadcountOpex = {
  category: string
  monthlyAmount: string
  startMonth: string
}

export type HeadcountPlanData = {
  forecastSetId: string
  name: string
  capturedAt: string
  source: string
  baselineMonthly: string | null
  hires: HeadcountHire[]
  opex: HeadcountOpex[]
}

// Publish input mirrors the GraphQL PublishHeadcountPlanInput. benefitsRate is
// optional (the backend defaults it); everything else is required per row.
export type PublishHeadcountInput = {
  name?: string
  baselineMonthly?: string | null
  hires: { role: string; annualSalary: string; benefitsRate: string; startMonth: string }[]
  opex: { category: string; monthlyAmount: string; startMonth: string }[]
}

// freee "Costs" tab. Money arrives as strings, same as everywhere else.
export type FreeeCostTransaction = {
  month: string
  issueDate: string
  entrySide: string
  amount: string
  freeeLabel: string | null
  effectiveLabel: string | null
  vendorRaw: string | null
  vendorKey: string
  displayName: string | null
  description: string | null
  refNumber: string | null
  freeeDealId: string
  costKey: string
}

export type FreeeCostsByLabelMonth = {
  month: string
  effectiveLabel: string
  totalAmount: string
  transactionCount: number
}

export type FreeeRecurringCost = {
  vendorKey: string
  displayName: string
  effectiveLabel: string
  freeeLabel: string | null
  labelOverride: string | null
  displayNameOverride: string | null
  occurrences: number
  distinctMonths: number
  firstMonth: string
  lastMonth: string
  totalAmount: string
  avgAmount: string
  latestAmount: string
  cadence: string
  lastPaymentDate: string
  // 'ongoing' | 'assumed_canceled' | 'undetermined' — inferred in the backend
  // view from the cadence and the last payment date.
  inferredStatus: string
  // Manual status label ('ongoing' | 'canceled'); null = trust the inference.
  statusOverride: string | null
  // The status after any manual override supersedes the inference. 'canceled'
  // only ever comes from a manual label — an inferred stop is 'assumed_canceled'.
  effectiveStatus: string
}

export type SetFreeeCostTagInput = {
  vendorKey: string
  displayName?: string | null
  labelOverride?: string | null
  statusOverride?: string | null
}

// 'full' = finance-viewer (the whole dashboard); 'it-costs' = finance-it-costs
// alone (IT Software Subscriptions costs only).
export type DashboardScope = 'full' | 'it-costs'

export type FreeeCostsFreshness = {
  lastSucceededAt: string | null
  lastRunAt: string | null
  lastRunStatus: string | null
  stale: boolean
}

export type ItCostsData = {
  itCostsByMonth: FreeeCostsByLabelMonth[]
  itRecurringCosts: FreeeRecurringCost[]
  itCostTransactions: FreeeCostTransaction[]
  itCostsFreshness: FreeeCostsFreshness
}

export type DashboardData = {
  gospelCashFlow: GospelMonth[]
  cashPositionByMonth: CashPositionMonth[]
  // The same three series with the payroll term from GOSPEL row 4 (what the
  // cash charts read today; src/payrollSource.ts picks one set).
  gospelCashFlowGospelAp: GospelMonth[]
  cashPositionByMonthGospelAp: CashPositionMonth[]
  balanceReconciliationGospelAp: BalanceRow[]
  softwarePlByMonth: SoftwarePlMonth[]
  balanceReconciliation: BalanceRow[]
  pipelineOverlapReport: OverlapRow[]
  sourceFreshness: Freshness[]
  headcountPlan: HeadcountPlanData | null
  headcountPlanReconciliation: ReconciliationMonth[]
  freeeCostsByLabelMonth: FreeeCostsByLabelMonth[]
  freeeRecurringCosts: FreeeRecurringCost[]
  freeeCostTransactions: FreeeCostTransaction[]
}

const DASHBOARD_QUERY = `
  query Dashboard($from: String, $to: String) {
    gospelCashFlow(from: $from, to: $to) {
      month
      actualAr
      actualFundraise
      actualAp
      forecastNetNewAr
      forecastNetNewAp
      netCashDifference
      arBasis
      apBasis
      planPayroll
    }
    cashPositionByMonth(from: $from, to: $to) {
      month
      netCashDifference
      cashPosition
      anchorMonth
      netCashDifferenceExPipeline
      cashPositionExPipeline
      anchorSource
      monthEnded
      actualBalance
      actualSource
      pointKind
      projectionAnchorMonth
      projectedBalance
      projectedBalanceExPipeline
    }
    softwarePlByMonth(from: $from, to: $to) {
      month
      softwareRevenue
      softwareCost
      softwareMargin
    }
    balanceReconciliation {
      month
      auditedBalance
      source
      computedBalance
      drift
    }
    gospelCashFlowGospelAp(from: $from, to: $to) {
      month
      actualAr
      actualFundraise
      actualAp
      forecastNetNewAr
      forecastNetNewAp
      netCashDifference
      arBasis
      apBasis
      planPayroll
    }
    cashPositionByMonthGospelAp(from: $from, to: $to) {
      month
      netCashDifference
      cashPosition
      anchorMonth
      netCashDifferenceExPipeline
      cashPositionExPipeline
      anchorSource
      monthEnded
      actualBalance
      actualSource
      pointKind
      projectionAnchorMonth
      projectedBalance
      projectedBalanceExPipeline
    }
    balanceReconciliationGospelAp {
      month
      auditedBalance
      source
      computedBalance
      drift
    }
    pipelineOverlapReport {
      kind
      businessLine
      prospect
      month
      expectedAmount
      weightedAmount
    }
    sourceFreshness {
      job
      lastSucceededAt
      lastRunAt
      lastRunStatus
      stale
      issues {
        check
        label
        month
        tab
        cell
        cellUrl
        statedValue
        computedValue
        blockSubtotalCell
        blockSubtotalCellUrl
        blockSubtotalValue
      }
    }
    headcountPlan {
      forecastSetId
      name
      capturedAt
      source
      baselineMonthly
      hires {
        role
        annualSalary
        benefitsRate
        startMonth
      }
      opex {
        category
        monthlyAmount
        startMonth
      }
    }
    headcountPlanReconciliation(from: $from, to: $to) {
      month
      freeeSalaryCost
      anchorMonth
      freeeAnchorCost
      actualGrowth
      plannedGrowth
      divergence
      status
    }
    freeeCostsByLabelMonth(from: $from, to: $to) {
      month
      effectiveLabel
      totalAmount
      transactionCount
    }
    freeeRecurringCosts {
      vendorKey
      displayName
      effectiveLabel
      freeeLabel
      labelOverride
      displayNameOverride
      occurrences
      distinctMonths
      firstMonth
      lastMonth
      totalAmount
      avgAmount
      latestAmount
      cadence
      lastPaymentDate
      inferredStatus
      statusOverride
      effectiveStatus
    }
    # No date window: the same full synced history freeeRecurringCosts (above)
    # sums over, so a stream's drill-down lines add up to its Total and its
    # "past year" can reach back before the dashboard's window start.
    freeeCostTransactions {
      month
      issueDate
      entrySide
      amount
      freeeLabel
      effectiveLabel
      vendorRaw
      vendorKey
      displayName
      description
      refNumber
      freeeDealId
      costKey
    }
  }
`

// The scoped page (a `finance-it-costs` user, no finance-viewer). Every field
// here is gated to that role server-side and read as finance_it_ro, so this
// query can only ever return IT Software Subscriptions costs, whatever it asks.
// Same shapes as the full query's freee fields, so the same components render it.
const IT_COSTS_QUERY = `
  query ItCosts($from: String, $to: String) {
    itCostsByMonth(from: $from, to: $to) {
      month
      effectiveLabel
      totalAmount
      transactionCount
    }
    itRecurringCosts {
      vendorKey
      displayName
      effectiveLabel
      freeeLabel
      labelOverride
      displayNameOverride
      occurrences
      distinctMonths
      firstMonth
      lastMonth
      totalAmount
      avgAmount
      latestAmount
      cadence
      lastPaymentDate
      inferredStatus
      statusOverride
      effectiveStatus
    }
    itCostTransactions {
      month
      issueDate
      entrySide
      amount
      freeeLabel
      effectiveLabel
      vendorRaw
      vendorKey
      displayName
      description
      refNumber
      freeeDealId
      costKey
    }
    itCostsFreshness {
      lastSucceededAt
      lastRunAt
      lastRunStatus
      stale
    }
  }
`

const ACCESS_QUERY = `
  query Access {
    dashboardAccess {
      scope
      canQuery
    }
  }
`

const SET_IT_COST_TAG_MUTATION = `
  mutation SetItCostTag($input: SetFreeeCostTagInput!) {
    setItCostTag(input: $input) {
      vendorKey
      displayName
      labelOverride
      statusOverride
      updatedAt
    }
  }
`

const SET_FREEE_COST_TAG_MUTATION = `
  mutation SetFreeeCostTag($input: SetFreeeCostTagInput!) {
    setFreeeCostTag(input: $input) {
      vendorKey
      displayName
      labelOverride
      statusOverride
      updatedAt
    }
  }
`

const PUBLISH_HEADCOUNT_MUTATION = `
  mutation PublishHeadcount($input: PublishHeadcountPlanInput!) {
    publishHeadcountPlan(input: $input) {
      forecastSetId
      name
      capturedAt
    }
  }
`

export type RefreshJobResult = {
  job: string
  status: string
  rowsRead: number | null
  rowsRejected: number | null
  message: string | null
}

const REFRESH_MUTATION = `
  mutation Refresh {
    refreshData {
      job
      status
      rowsRead
      rowsRejected
      message
    }
  }
`

// Thrown when the caller's session is no longer valid — an expired or rejected
// token. The app catches this specifically to sign the user out and return them
// to the login screen, rather than showing the raw message. Deliberately
// distinct from a 403: that is a missing role, which signing back in cannot fix.
export class SessionExpiredError extends Error {
  constructor(message = 'Your session expired. Please sign in again.') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

type GraphQLErrorEntry = { message: string; extensions?: { code?: string } }
type GraphQLResponse<T> = {
  data?: T
  errors?: GraphQLErrorEntry[]
}

// On the GraphQL path the ZitadelGuard rejects an expired/invalid token as an
// error in the 200 body (GraphQL convention), not a 401. The backend tags those
// with extensions.code = UNAUTHENTICATED (finance-error-formatter.ts); the
// message set is a fallback so re-login still works if an older backend, without
// that formatter, is deployed. Only per-credential rejections belong here: a 403
// (missing role) and a 503 (server auth misconfigured) are deliberately absent —
// re-login can fix neither, and looping the user through it would be worse than
// the plain error.
const AUTH_ERROR_MESSAGES: ReadonlySet<string> = new Set(['invalid token', 'missing bearer token'])

export function isAuthError(error: GraphQLErrorEntry): boolean {
  if (error.extensions?.code === 'UNAUTHENTICATED') return true
  return AUTH_ERROR_MESSAGES.has(error.message.trim().toLowerCase())
}

async function graphql<T>(apiUrl: string, accessToken: string, body: object): Promise<T> {
  const response = await fetch(`${apiUrl.replace(/\/$/, '')}/graphql`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (response.status === 401) throw new SessionExpiredError()
  if (response.status === 403) {
    throw new Error('Signed in, but this account lacks the FINANCE_VIEWER role in Zitadel.')
  }
  if (!response.ok) throw new Error(`API returned ${response.status}`)

  const payload = (await response.json()) as GraphQLResponse<T>
  // GraphQL reports errors with a 200, so a partial response must not be
  // rendered as if it were whole — half a masthead looks like a real one.
  if (payload.errors && payload.errors.length > 0) {
    // An expired/rejected token surfaces here (200 + errors); route it to the
    // re-login path instead of printing "invalid token" at the reader.
    if (payload.errors.some(isAuthError)) throw new SessionExpiredError()
    throw new Error(payload.errors.map((e) => e.message).join('; '))
  }
  if (!payload.data) throw new Error('API returned no data')
  return payload.data
}

// Kick off a live ingest of all three sources, then let the caller re-read.
export async function triggerRefresh(apiUrl: string, accessToken: string): Promise<RefreshJobResult[]> {
  const data = await graphql<{ refreshData: RefreshJobResult[] }>(apiUrl, accessToken, {
    query: REFRESH_MUTATION,
  })
  return data.refreshData
}

// Publish a new version of the headcount & AP plan. Returns the new version's
// id/name/timestamp; the caller re-reads the dashboard to pick up the plan and
// the gospel figures it feeds.
export async function publishHeadcountPlan(
  apiUrl: string,
  accessToken: string,
  input: PublishHeadcountInput,
): Promise<{ forecastSetId: string; name: string; capturedAt: string }> {
  const data = await graphql<{ publishHeadcountPlan: { forecastSetId: string; name: string; capturedAt: string } }>(
    apiUrl,
    accessToken,
    { query: PUBLISH_HEADCOUNT_MUTATION, variables: { input } },
  )
  return data.publishHeadcountPlan
}

export type SavedFreeeCostTag = {
  vendorKey: string
  displayName: string | null
  labelOverride: string | null
  statusOverride: string | null
}

// Set (or clear, when every field is blank) the display-name, label and
// recurring-status override for a freee vendor stream. Returns the saved tag,
// or null when cleared. The caller re-reads the dashboard to pick up the new
// effective labels/names/statuses.
export async function setFreeeCostTag(
  apiUrl: string,
  accessToken: string,
  input: SetFreeeCostTagInput,
): Promise<SavedFreeeCostTag | null> {
  const data = await graphql<{ setFreeeCostTag: SavedFreeeCostTag | null }>(apiUrl, accessToken, {
    query: SET_FREEE_COST_TAG_MUTATION,
    variables: { input },
  })
  return data.setFreeeCostTag
}

// The scoped page's edit: the same override, refused server-side unless the
// vendor is currently in the IT scope.
export async function setItCostTag(
  apiUrl: string,
  accessToken: string,
  input: SetFreeeCostTagInput,
): Promise<SavedFreeeCostTag | null> {
  const data = await graphql<{ setItCostTag: SavedFreeeCostTag | null }>(apiUrl, accessToken, {
    query: SET_IT_COST_TAG_MUTATION,
    variables: { input },
  })
  return data.setItCostTag
}

export type DashboardAccess = {
  scope: DashboardScope
  // finance-query as well as finance-viewer: the SQL tab is offered.
  canQuery: boolean
}

// Which page the signed-in user's roles allow. Asked first on every load: the
// full query is refused outright for a scoped user. It decides only what the
// app RENDERS; each data query (and POST /query) is gated on its own.
export async function fetchAccess(apiUrl: string, accessToken: string): Promise<DashboardAccess> {
  const data = await graphql<{ dashboardAccess: { scope: string; canQuery: boolean } }>(apiUrl, accessToken, {
    query: ACCESS_QUERY,
  })
  const { scope, canQuery } = data.dashboardAccess
  if (scope !== 'full' && scope !== 'it-costs') throw new Error(`Unknown dashboard access scope: ${scope}`)
  return { scope, canQuery }
}

// The governed SQL endpoint's answer (backend query.service.ts): SELECT-only,
// one statement, run as finance_ro with a timeout and a row cap.
export type QueryResult = {
  columns: string[]
  rows: unknown[][]
  rowCount: number
  truncated: boolean
  elapsedMs: number
}

// Run a read-only statement through POST /query. REST, not GraphQL, by the
// backend's design; the same bearer token, and the server requires the
// finance-query role on every call.
export async function runQuery(
  apiUrl: string,
  accessToken: string,
  statement: string,
  limit?: number,
): Promise<QueryResult> {
  const response = await fetch(`${apiUrl.replace(/\/$/, '')}/query`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(limit === undefined ? { statement } : { statement, limit }),
  })
  if (response.status === 401) throw new SessionExpiredError()
  if (response.status === 403) throw new Error('This account lacks the finance-query role in Zitadel.')
  if (response.status === 400) {
    // A statement the guard refused: its reason quotes the rule, safe to show.
    const body = (await response.json().catch(() => null)) as { message?: unknown } | null
    throw new Error(typeof body?.message === 'string' ? body.message : 'The statement was rejected.')
  }
  if (response.status >= 500 && response.status < 600) {
    // The server deliberately does not echo database error text to a browser,
    // so a SQL mistake and an outage look alike from here. Say which it likely is.
    throw new Error(
      `The query failed on the server (${response.status}). Usually a SQL error: an unknown column or table, a table finance_ro cannot read, or the statement timeout. The database's message is not returned to the browser.`,
    )
  }
  if (!response.ok) throw new Error(`API returned ${response.status}`)
  const payload: unknown = await response.json().catch(() => null)
  if (!isQueryResult(payload)) throw new Error('The query endpoint returned an unexpected response.')
  return payload
}

function isQueryResult(value: unknown): value is QueryResult {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    Array.isArray(v['columns']) &&
    Array.isArray(v['rows']) &&
    v['rows'].every(Array.isArray) &&
    typeof v['rowCount'] === 'number' &&
    typeof v['truncated'] === 'boolean' &&
    typeof v['elapsedMs'] === 'number'
  )
}

export async function fetchItCosts(
  apiUrl: string,
  accessToken: string,
  from: string,
  to: string,
): Promise<ItCostsData> {
  return graphql<ItCostsData>(apiUrl, accessToken, {
    query: IT_COSTS_QUERY,
    variables: { from, to },
  })
}

export async function fetchDashboard(
  apiUrl: string,
  accessToken: string,
  from: string,
  to: string,
): Promise<DashboardData> {
  return graphql<DashboardData>(apiUrl, accessToken, {
    query: DASHBOARD_QUERY,
    variables: { from, to },
  })
}
